import React, { useState, useEffect } from "react";
import departmentService from "../../services/departmentService";
import universityService from "../../services/universityService";
import campusService from "../../services/campusService";
import facultyService from "../../services/facultyService";
import { useNotification } from "../../context/NotificationContext";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";

const DepartmentList = ({ onEdit, onView, refreshTrigger }) => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUniversity, setSelectedUniversity] = useState("");
  const [selectedCampus, setSelectedCampus] = useState("");
  const [selectedFaculty, setSelectedFaculty] = useState("");

  // Dropdown data
  const [universities, setUniversities] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [universityLoading, setUniversityLoading] = useState(false);
  const [campusLoading, setCampusLoading] = useState(false);
  const [facultyLoading, setFacultyLoading] = useState(false);

  const { showSuccess } = useNotification();

  // Load universities on component mount
  useEffect(() => {
    loadUniversities();
  }, []);

  // Load campuses when university is selected
  useEffect(() => {
    if (selectedUniversity) {
      loadCampusesByUniversity(selectedUniversity);
    } else {
      setCampuses([]);
      setSelectedCampus("");
      setFaculties([]);
      setSelectedFaculty("");
    }
  }, [selectedUniversity]);

  // Load faculties when campus is selected
  useEffect(() => {
    if (selectedCampus) {
      loadFaculties(selectedCampus);
    } else {
      setFaculties([]);
      setSelectedFaculty("");
    }
  }, [selectedCampus]);

  // Load departments when filters change
  useEffect(() => {
    loadDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    pageSize,
    searchTerm,
    selectedUniversity,
    selectedCampus,
    selectedFaculty,
    refreshTrigger,
  ]);

  const loadUniversities = async () => {
    try {
      setUniversityLoading(true);
      const response = await universityService.getUniversities(0, 1000); // Get all universities
      if (response.success) {
        setUniversities(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading universities:", error);
    } finally {
      setUniversityLoading(false);
    }
  };

  const loadCampusesByUniversity = async (universityId) => {
    try {
      setCampusLoading(true);
      const response = await campusService.getCampuses(
        0,
        1000,
        "",
        universityId
      ); // Get campuses for selected university
      if (response.success) {
        setCampuses(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading campuses:", error);
      setCampuses([]);
    } finally {
      setCampusLoading(false);
    }
  };

  const loadFaculties = async (campusId) => {
    try {
      setFacultyLoading(true);
      const response = await facultyService.getFaculties(0, 1000, "", campusId); // Get faculties for selected campus
      if (response.success) {
        setFaculties(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading faculties:", error);
      setFaculties([]);
    } finally {
      setFacultyLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const response = await departmentService.getDepartments(
        currentPage,
        pageSize,
        searchTerm,
        selectedCampus,
        selectedFaculty
      );

      if (response.success) {
        setDepartments(response.data.data || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (error) {
      console.error("Error loading departments:", error);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (department) => {
    if (
      window.confirm(`Are you sure you want to delete "${department.nameEn}"?`)
    ) {
      try {
        setDeleting(department.id);
        const response = await departmentService.deleteDepartment(
          department.id
        );

        if (response.success) {
          showSuccess("Department deleted successfully!");
          loadDepartments();
        }
      } catch (error) {
        console.error("Error deleting department:", error);
      } finally {
        setDeleting(null);
      }
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handleUniversityChange = (e) => {
    setSelectedUniversity(e.target.value);
    setSelectedCampus(""); // Reset campus when university changes
    setSelectedFaculty(""); // Reset faculty when university changes
    setCurrentPage(0);
  };

  const handleCampusChange = (e) => {
    setSelectedCampus(e.target.value);
    setSelectedFaculty(""); // Reset faculty when campus changes
    setCurrentPage(0);
  };

  const handleFacultyChange = (e) => {
    setSelectedFaculty(e.target.value);
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedUniversity("");
    setSelectedCampus("");
    setSelectedFaculty("");
    setCurrentPage(0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const startPage = Math.max(0, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`btn ${
            i === currentPage ? "btn-primary" : "btn-outline-primary"
          } btn-sm mx-1`}
          onClick={() => handlePageChange(i)}
        >
          {i + 1}
        </button>
      );
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Showing {currentPage * pageSize + 1} to{" "}
          {Math.min((currentPage + 1) * pageSize, totalElements)} of{" "}
          {totalElements} entries
        </div>
        <div className="d-flex align-items-center">
          <button
            className="btn btn-outline-primary btn-sm me-2"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            Previous
          </button>
          {pages}
          <button
            className="btn btn-outline-primary btn-sm ms-2"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages - 1}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="department-list">
      <ListToolbar
        title="Departments"
        subtitle={`${totalElements} department${totalElements !== 1 ? "s" : ""} found`}
        filterBadges={
          (searchTerm || selectedUniversity || selectedCampus || selectedFaculty) ? (
            <>
              {searchTerm && (
                <span className="badge bg-primary">Search: "{searchTerm}"</span>
              )}
              {selectedUniversity && (
                <span className="badge bg-secondary">
                  University:{" "}
                  {universities.find((u) => u.id.toString() === selectedUniversity)?.nameEn}
                </span>
              )}
              {selectedCampus && (
                <span className="badge bg-info">
                  Campus:{" "}
                  {campuses.find((c) => c.id.toString() === selectedCampus)?.nameEn}
                </span>
              )}
              {selectedFaculty && (
                <span className="badge bg-success">
                  Faculty:{" "}
                  {faculties.find((f) => f.id.toString() === selectedFaculty)?.nameEn}
                </span>
              )}
            </>
          ) : null
        }
      >
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label fw-medium">Search Departments</label>
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0">
                <i className="fas fa-search text-muted"></i>
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          <div className="col-md-4">
            <label className="form-label fw-medium">Filter by University</label>
            <select
              className="form-select"
              value={selectedUniversity}
              onChange={handleUniversityChange}
              disabled={universityLoading}
            >
              <option value="">All Universities</option>
              {universities.map((university) => (
                <option key={university.id} value={university.id}>
                  {university.nameEn}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label fw-medium">Filter by Campus</label>
            <select
              className="form-select"
              value={selectedCampus}
              onChange={handleCampusChange}
              disabled={campusLoading || !selectedUniversity}
            >
              <option value="">
                {!selectedUniversity ? "Select University First" : "All Campuses"}
              </option>
              {campuses.map((campus) => (
                <option key={campus.id} value={campus.id}>
                  {campus.nameEn} - {campus.city}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label fw-medium">Filter by Faculty</label>
            <select
              className="form-select"
              value={selectedFaculty}
              onChange={handleFacultyChange}
              disabled={facultyLoading || !selectedCampus}
            >
              <option value="">
                {!selectedCampus ? "Select Campus First" : "All Faculties"}
              </option>
              {faculties.map((faculty) => (
                <option key={faculty.id} value={faculty.id}>
                  {faculty.nameEn}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-2 d-flex align-items-end">
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0">Show:</label>
              <select
                className="form-select form-select-sm"
                value={pageSize}
                onChange={handlePageSizeChange}
                style={{ width: "auto" }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          {(searchTerm || selectedUniversity || selectedCampus || selectedFaculty) && (
            <div className="col-auto d-flex align-items-end">
              <button className="btn btn-outline-secondary btn-sm" onClick={clearFilters}>
                <i className="fas fa-times me-1"></i>
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </ListToolbar>

      {/* Departments Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading departments...</p>
            </div>
          ) : departments.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="fas fa-sitemap text-muted mb-3"
                style={{ fontSize: "3rem" }}
              ></i>
              <h5 className="text-muted">No Departments Found</h5>
              <p className="text-muted">
                {searchTerm || selectedCampus || selectedFaculty
                  ? "Try adjusting your filters to find departments."
                  : "No departments have been created yet."}
              </p>
            </div>
          ) : (
            <AppTable responsive>
                <thead>
                  <tr>
                    <th className="ps-4">Department</th>
                    <th>Faculty</th>
                    <th>Campus</th>
                    <th className="text-center">Students</th>
                    <th className="text-center">Status</th>
                    <th>Created</th>
                    <th className="text-center pe-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {departments.map((department) => (
                    <tr key={department.id} className="border-bottom">
                      <td className="ps-4">
                        <div className="d-flex align-items-center">
                          <div
                            className={`department-icon rounded-circle d-flex align-items-center justify-content-center me-3 ${
                              department.status
                                ? "bg-primary bg-opacity-10"
                                : "bg-secondary bg-opacity-10"
                            }`}
                            style={{ width: "40px", height: "40px" }}
                          >
                            <i
                              className={`fas fa-sitemap ${
                                department.status
                                  ? "text-primary"
                                  : "text-secondary"
                              }`}
                            ></i>
                          </div>
                          <div>
                            <div className="fw-bold text-dark">
                              {department.nameEn}
                            </div>
                            <small className="text-muted">
                              {department.nameLocal}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="text-dark fw-medium">
                          {department.facultyName}
                        </div>
                      </td>
                      <td>
                        <div className="text-dark">{department.campusName}</div>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-info bg-opacity-10 text-info fw-bold">
                          {department.studentCount || 0}
                        </span>
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge ${
                            department.status
                              ? "bg-success bg-opacity-10 text-success"
                              : "bg-danger bg-opacity-10 text-danger"
                          } fw-bold`}
                        >
                          <i
                            className={`fas ${
                              department.status
                                ? "fa-check-circle"
                                : "fa-times-circle"
                            } me-1`}
                          ></i>
                          {department.status ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <span className="text-muted">
                          {formatDate(department.createdAt)}
                        </span>
                      </td>
                      <td className="text-center pe-4">
                        <div className="btn-group" role="group">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => onView(department)}
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={() => onEdit(department)}
                            title="Edit Department"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(department)}
                            disabled={deleting === department.id}
                            title="Delete Department"
                          >
                            {deleting === department.id ? (
                              <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            ) : (
                              <i className="fas fa-trash"></i>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
            </AppTable>
          )}
        </div>
      </div>

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
};

export default DepartmentList;
